import { datadogRum } from '@datadog/browser-rum';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { SessionProvider } from 'next-auth/react';
import { SWRConfig } from 'swr';

import paths from 'src/common/paths.json';
import Layout from 'src/components/Layout/Layout';

import Auth from './_auth';
import AxiosInterceptors from './_axiosInterceptors';
import 'src/styles/global.scss';

const { publicRuntimeConfig } = getConfig();

if (['production', 'staging'].includes(publicRuntimeConfig.APP_ENV)) {
  datadogRum.init({
    ...publicRuntimeConfig.datadogRumConfig,
    forwardErrorsToLogs: true, // Ensure error tracking is enabled
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true
  });

  datadogRum.startSessionReplayRecording();
}

function App({ Component, pageProps, router }: AppProps) {
  if (router.pathname === paths.CHECK) {
    return <Component />;
  }

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="stylesheet" href="https://use.typekit.net/mui0guq.css" />
      </Head>

      <SessionProvider session={pageProps.session}>
        <SWRConfig
          value={{
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
          }}
        >
          <Auth>
            <AxiosInterceptors>
              <Layout>
                <Component {...pageProps} />
              </Layout>
            </AxiosInterceptors>
          </Auth>
        </SWRConfig>
      </SessionProvider>
    </>
  );
}

export function reportWebVitals({ name, value }: NextWebVitalsMetric) {
  if (['production', 'staging'].includes(publicRuntimeConfig.APP_ENV)) {
    datadogRum.addTiming(name, value);
  }
}

export default App;